import React, { useState, useCallback } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useSocial } from "../../contexts/SocialContext";
import { useAuth } from "../../contexts/AuthContext";
import * as api from "../../services/api";
import {
  Heart,
  MessageCircle,
  Send,
  ClipboardList,
  Dumbbell,
  Loader2,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { summariseWorkoutPost } from "../../utils/feedSummary";
import { timeAgo } from "../../utils/time";
import DeletePostDialog from "./DeletePostDialog";
import TrainedTogetherDialog from "./TrainedTogetherDialog";
import SessionPartners from "./SessionPartners";
import MetricChips from "./MetricChips";
import WorkoutPostDialog from "./WorkoutPostDialog";
import CommentItem from "./CommentItem";

export default function FeedPostCard({ post, onUpdate }) {
  const { toggleLike, deletePost } = useSocial();
  const { user } = useAuth();
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [togetherVariant, setTogetherVariant] = useState(null);
  const [viewingPlan, setViewingPlan] = useState(false);

  const loadComments = useCallback(async () => {
    setCommentsLoading(true);
    try {
      const data = await api.getComments(post.id);
      setComments(data);
    } catch (err) {
      console.error("Load comments failed:", err);
    } finally {
      setCommentsLoading(false);
    }
  }, [post.id]);

  const handleToggleComments = () => {
    if (!showComments) {
      loadComments();
    }
    setShowComments(!showComments);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      await api.addComment(post.id, newComment.trim());
      setNewComment("");
      await loadComments();
    } catch (err) {
      console.error("Add comment failed:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async () => {
    await toggleLike(post.id);
  };

  // Workouts post themselves now (issue #79), so taking one back has to be
  // possible from the feed itself — deletePost had existed in the API and the
  // context since the start with nothing calling it.
  const isMine = user?.id != null && Number(post.user_id) === Number(user.id);
  const summary = summariseWorkoutPost(post);
  const handleDelete = async () => {
    setConfirmingDelete(false);
    setDeleting(true);
    try {
      await deletePost(post.id);
      onUpdate?.();
    } catch (err) {
      console.error("Delete post failed:", err);
      setDeleting(false);
    }
  };

  // Linking sessions needs a session to link. A written post with no workout
  // behind it has nothing to join to anyone else's.
  const canLink = post.workout_session_id != null;
  // One claim per pair per workout, so the offer disappears once you are on
  // the card — confirmed, waiting, or asked.
  const alreadyInvolved = (post.partners || []).some(
    (p) => Number(p.user_id) === Number(user?.id),
  );
  // Only a post carrying a plan snapshot has anything to open. An imported
  // ride still shows its summary — it just isn't a door to anywhere.
  const canViewPlan = Boolean(post.plan_data);

  // The summary reads identically whether or not it is clickable, so it is
  // built once and wrapped in whichever element is honest about that.
  const summaryBody = (
    <>
      {summary.title && (
        <div className="flex items-center gap-2">
          <Dumbbell className="h-4 w-4 text-primary flex-shrink-0" />
          <span className="text-sm font-medium">{summary.title}</span>
        </div>
      )}

      <MetricChips metrics={summary.metrics} />

      {summary.movements.length > 0 && (
        <p className="text-xs text-foreground/70 leading-snug">
          {summary.movements.join(" · ")}
          {summary.moreMovements > 0 && (
            <span className="text-muted-foreground">
              {" "}
              +{summary.moreMovements} more
            </span>
          )}
        </p>
      )}

      {summary.notes && (
        <p className="text-xs italic text-muted-foreground border-l-2 border-border pl-2 leading-snug">
          {summary.notes}
        </p>
      )}
    </>
  );

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          {post.author_photo ? (
            <img src={post.author_photo} alt="" className="h-8 w-8 rounded-full" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
              {post.author_name?.[0] || "?"}
            </div>
          )}
          <div className="flex-1">
            <p className="text-sm font-medium">{post.author_name}</p>
            <p className="text-xs text-muted-foreground">{timeAgo(post.created_at)}</p>
          </div>
        </div>

        {/* Content */}
        {post.content && <p className="text-sm mb-3">{post.content}</p>}

        {/* Workout summary. Driven by the session, not just the plan snapshot:
            a post whose plan is missing still has a title, a duration and
            whatever the watch recorded, and showing a bare byline instead was
            the bug. */}
        {summary.hasDetail &&
          (canViewPlan ? (
            // A real button, not a div with an onClick: opening the plan has to
            // work from a keyboard and announce itself to a screen reader.
            <button
              type="button"
              onClick={() => setViewingPlan(true)}
              aria-label={`View the full plan for ${summary.title || "this workout"}`}
              className="w-full text-left bg-muted/30 hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none rounded-lg p-3 mb-3 space-y-2 transition-colors"
            >
              {summaryBody}
            </button>
          ) : (
            <div className="bg-muted/30 rounded-lg p-3 mb-3 space-y-2">
              {summaryBody}
            </div>
          ))}

        {/* Who else was there. Sits below the workout and above the actions,
            because it is part of what the post says, not something you do to
            it. */}
        <SessionPartners post={post} onUpdate={onUpdate} />

        {/* Actions. Wraps: with a viewable plan and a linkable session this row
            carries five controls, which is wider than a phone. */}
        <div className="flex flex-wrap items-center gap-1 pt-2 border-t border-border">
          <Button variant="ghost" size="sm" className={post.liked_by_me ? "text-red-500" : ""} onClick={handleLike}>
            <Heart className={`h-4 w-4 mr-1 ${post.liked_by_me ? "fill-current" : ""}`} />
            {parseInt(post.like_count) || ""}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleToggleComments}>
            <MessageCircle className="h-4 w-4 mr-1" />
            {parseInt(post.comment_count) || ""}
          </Button>
          {canViewPlan && (
            <Button variant="ghost" size="sm" onClick={() => setViewingPlan(true)}>
              <ClipboardList className="h-4 w-4 mr-1" />
              View
            </Button>
          )}
          {/* Same claim from two ends: name your partners on your own workout,
              or put your hand up on someone else's. */}
          {canLink && isMine && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setTogetherVariant("tag")}
              title="Say who you trained with"
            >
              <Users className="h-4 w-4 mr-1" />
              Trained with
            </Button>
          )}
          {canLink && !isMine && !alreadyInvolved && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setTogetherVariant("claim")}
              title="Say you were at this workout"
            >
              <UserPlus className="h-4 w-4 mr-1" />
              I was there
            </Button>
          )}
          {isMine && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto text-muted-foreground hover:text-destructive"
              onClick={() => setConfirmingDelete(true)}
              disabled={deleting}
              title="Remove from feed"
              aria-label="Remove from feed"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>

        {/* Comments */}
        {showComments && (
          <div className="mt-3 pt-3 border-t border-border space-y-3">
            {commentsLoading ? (
              <div className="flex justify-center py-2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : (
              comments.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  postId={post.id}
                  isMine={user?.id != null && Number(comment.user_id) === Number(user.id)}
                  onChanged={async () => {
                    await loadComments();
                    onUpdate?.();
                  }}
                />
              ))
            )}
            <div className="flex gap-2">
              <Input
                placeholder="Write a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
                className="text-sm"
              />
              <Button size="sm" onClick={handleAddComment} disabled={submitting || !newComment.trim()}>
                {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              </Button>
            </div>
          </div>
        )}

        <DeletePostDialog
          open={confirmingDelete}
          onOpenChange={setConfirmingDelete}
          onConfirm={handleDelete}
          title={summary.title}
        />

        <TrainedTogetherDialog
          open={togetherVariant !== null}
          onOpenChange={(next) => !next && setTogetherVariant(null)}
          variant={togetherVariant || "tag"}
          post={post}
        />

        <WorkoutPostDialog
          post={post}
          open={viewingPlan}
          onOpenChange={setViewingPlan}
        />
      </CardContent>
    </Card>
  );
}
